import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Chip,
  Button,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { EyeIcon, RotateCcw, XIcon, Calendar } from "lucide-react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDispatch, useSelector } from "react-redux";
import { setLimit, setPage } from "../../redux/voucher/voucherSlice";
import { useGetList } from "../../hooks/useGetList";
import buildParams from "../../utils/buildParams";
import formatDate from "../../utils/formatDate";
import { useNavigate } from "react-router-dom";

const VoucherList = ({ show }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelDate, setCancelDate] = useState(new Date());
  const [cancelReason, setCancelReason] = useState("");
  const filter = useSelector((state) => state.voucher);
  const params = buildParams(filter);

  const { data, loading, fetchData } = useGetList(
    `/head-office/vouchers`,
    true,
    Object.keys(params).length ? params : undefined,
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Demo data
  const vouchers = [
    {
      id: 1,
      date: "2024-01-15",
      code: "VOU001",
      customerName: "John Doe",
      customerPhone: "09123456789",
      doctor: "Dr. Smith",
      totalAmount: 150000,
      paidAmount: 150000,
      refundAmount: 0,
      balance: 0,
      paymentMethod: "Cash",
      status: "completed",
    },
    {
      id: 2,
      date: "2024-01-16",
      code: "VOU002",
      customerName: "Jane Smith",
      customerPhone: "09876543210",
      doctor: "Dr. Johnson",
      totalAmount: 200000,
      paidAmount: 150000,
      refundAmount: 0,
      balance: 50000,
      paymentMethod: "Card",
      status: "partial",
    },
    {
      id: 3,
      date: "2024-01-17",
      code: "VOU003",
      customerName: "Robert Brown",
      customerPhone: "09555111222",
      doctor: "Dr. Williams",
      totalAmount: 120000,
      paidAmount: 120000,
      refundAmount: 50000,
      balance: 0,
      paymentMethod: "Bank Transfer",
      status: "refunded",
    },
    {
      id: 4,
      date: "2024-01-18",
      code: "VOU004",
      customerName: "Emily Davis",
      customerPhone: "09777888999",
      doctor: "Dr. Brown",
      totalAmount: 180000,
      paidAmount: 100000,
      refundAmount: 0,
      balance: 80000,
      paymentMethod: "Cash",
      status: "partial",
    },
    {
      id: 5,
      date: "2024-01-19",
      code: "VOU005",
      customerName: "Michael Wilson",
      customerPhone: "09444555666",
      doctor: "Dr. Davis",
      totalAmount: 95000,
      paidAmount: 95000,
      refundAmount: 0,
      balance: 0,
      paymentMethod: "Mobile Payment",
      status: "completed",
    },
  ];

  const handleMenuClick = (event, voucher) => {
    setAnchorEl(event.currentTarget);
    setSelectedVoucher(voucher);
  };

  console.log(selectedVoucher);

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVoucher(null);
  };

  const handleView = () => {
    console.log("test");
    navigate(`/voucher/${selectedVoucher?._id}`);
    handleMenuClose();
  };
  const handleRefund = () => {
    console.log("Refund voucher:", selectedVoucher);
    navigate(`/voucher/refund/${selectedVoucher?._id}`);
    handleMenuClose();
  };

  const handleCancel = () => {
    setCancelDate(new Date());
    setCancelReason("");
    setCancelDialogOpen(true);
    setAnchorEl(null); // Only close the menu, keep selectedVoucher
  };

  const handleConfirmCancel = () => {
    const payload = {
      voucher: selectedVoucher,
      cancelDate: cancelDate,
      reason: cancelReason,
    };
    console.log(payload);
    fetchData();
    setCancelDialogOpen(false);
    setSelectedVoucher(null);
    setCancelReason("");
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedVoucher(null);
    setCancelReason("");
  };

  const handleChangeRowsPerPage = (e) => {
    dispatch(setLimit(Number(e.target.value)));
    dispatch(setPage(1));
  };

  const handleChangePage = (e, page) => {
    dispatch(setPage(page + 1));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "partial":
        return "warning";
      case "refunded":
        return "info";
      default:
        return "default";
    }
  };


  useEffect(() => {
    const params = buildParams(filter);
    if (Object.keys(params).length > 0) {
      fetchData(undefined, params);
    } else {
      fetchData();
    }
  }, [filter]);


  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Voucher List
      </Typography>
    

      <TableContainer
        component={Paper}
        sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        <Table sx={{ minWidth: 1200 }} size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ width: "60px", fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ width: "200px", fontWeight: 600 }}>
                Voucher
              </TableCell>
              <TableCell sx={{ width: "220px", fontWeight: 600 }}>
                Customer
              </TableCell>
              <TableCell sx={{ width: "160px", fontWeight: 600 }}>
                Doctor
              </TableCell>
              <TableCell sx={{ width: "130px", fontWeight: 600 }}>
                TotalAmount
              </TableCell>
              <TableCell sx={{ width: "130px", fontWeight: 600 }}>
                FirstPaidAmount
              </TableCell>
              <TableCell sx={{ width: "130px", fontWeight: 600 }}>
                FirstPaymentType
              </TableCell>
              <TableCell sx={{ width: "130px", fontWeight: 600 }}>
                SecondPaidAmount
              </TableCell>
              <TableCell sx={{ width: "130px", fontWeight: 600 }}>
                SecondPaymentType
              </TableCell>
              <TableCell sx={{ width: "130px", fontWeight: 600 }}>
                RefundAmount
              </TableCell>
              <TableCell sx={{ width: "120px", fontWeight: 600 }}>
                Balance
              </TableCell>
              <TableCell sx={{ width: "140px", fontWeight: 600 }}>
                PaymentMethod
              </TableCell>
              {show !== "isCancel" && (
                <TableCell sx={{ width: "120px", fontWeight: 600 }}>
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={30} sx={{ color: "#1976d2" }} />
                    <div>Loading vouchers...</div>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data?.data?.length > 0 ? (
              data?.data?.map((voucher, index) => (
                <TableRow
                  key={voucher.id}
                  sx={{
                    "&:hover": { backgroundColor: "#f9f9f9" },
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        {voucher.code}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Calendar size={12} color="#666" />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                          }}
                        >
                          {formatDate(
                            voucher?.createdAt ?? new Date().toISOString(),
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: 400,
                        }}
                      >
                        {voucher?.relatedPatient?.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "13px",
                          color: "#000",
                        }}
                      >
                        {voucher?.relatedPatient?.phone}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{voucher?.relatedDoctor?.name ?? "N/A"}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {voucher.totalAmount.toLocaleString()}
                  </TableCell>
                  <TableCell
                    sx={{ color: (voucher.firstPaidAmount || 0) > 0 ? "green" : "inherit" }}
                  >
                    {(voucher.firstPaidAmount || 0).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {voucher.paymentType || "N/A"}
                  </TableCell>
                  <TableCell
                    sx={{ color: (voucher.secondAmount || 0) > 0 ? "green" : "inherit" }}
                  >
                    {(voucher.secondAmount || 0).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {voucher.secondAccountType || "N/A"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: voucher.refundAmount > 0 ? "orange" : "inherit",
                    }}
                  >
                    {voucher.refundAmount.toLocaleString()}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: voucher.balance > 0 ? "red" : "green",
                    }}
                  >
                    {voucher.balance.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={voucher.paymentMethod}
                      size="small"
                      color={
                        voucher.paymentMethod === "Paid" ? "success" : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={(event) => handleMenuClick(event, voucher)}
                      size="small"
                      variant="outlined"
                      endIcon={<KeyboardArrowDownIcon />}
                      sx={{
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        "&:hover": {
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          backgroundColor: "#f5f5f5",
                        },
                        textTransform: "none",
                        fontSize: "13px",
                      }}
                    >
                      Actions
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      sx={{
                        "& .MuiMenuItem-root": {
                          flexGrow: 1,
                        },
                      }}
                      slotProps={{
                        paper: {
                          elevation: 0,
                          sx: {
                            mt: 1,
                            borderRadius: 2,
                            minWidth: 140,
                            fontSize: 12,
                            border: "1px solid #f0f0f0",
                          },
                        },
                      }}
                    >
                      <MenuItem onClick={handleView}>
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <EyeIcon className="size-10" />
                          View
                        </Typography>
                      </MenuItem>

                      {!selectedVoucher?.Refund && (
                        <MenuItem onClick={handleRefund}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <RotateCcw className="size-10" />
                            Refund
                          </Typography>
                        </MenuItem>
                      )}

                      {/* {show !== "isCancel" && (
                        <MenuItem onClick={handleCancel}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <XIcon className="size-10" />
                            Cancel
                          </Typography>
                        </MenuItem>
                      )} */}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  There is no voucher...
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
           rowsPerPageOptions={[5, 10, 15, 20, 25, 100]}
            component="div"
            count={data?.pagination?.total || 0}
            rowsPerPage={Number(filter?.limit || 10)}
            page={(filter?.page || 1) - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",

            "& .MuiTablePagination-toolbar": {
              display: "flex",
              alignItems: "center",
              minHeight: "52px",
            },

            "& .MuiTablePagination-selectLabel": {
              margin: 0,
              display: "flex",
              alignItems: "center",
            },

            "& .MuiTablePagination-displayedRows": {
              margin: 0,
              display: "flex",
              alignItems: "center",
            },

            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </TableContainer>

      {/* Cancel Modal Box */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Cancel Voucher
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* Voucher Code Field */}
            <TextField
              label="Voucher Code"
              value={selectedVoucher?.code || ""}
              disabled
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000",
                  color: "#000",
                },
              }}
            />

            {/* Voucher Date Field */}
            <TextField
              label="Voucher Date"
              type="date"
              value={
                cancelDate instanceof Date
                  ? cancelDate.toISOString().split("T")[0]
                  : cancelDate
              }
              onChange={(e) => setCancelDate(new Date(e.target.value))}
              fullWidth
              size="small"
              required
              InputLabelProps={{
                shrink: true,
              }}
            />

            {/* Reason Field */}
            <TextField
              label="Reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Enter reason for cancellation..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleCancelDialogClose}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            disabled={!cancelReason.trim()}
          >
            Cancel Voucher
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoucherList;
